import { TID } from '@/lib/testIds';

const steps = [
  { key: 'discover', number: '01', title: 'Discover', desc: 'Understand the business, people, market, and objectives.' },
  { key: 'diagnose', number: '02', title: 'Diagnose', desc: 'Identify root causes, opportunities, and constraints.' },
  { key: 'design',   number: '03', title: 'Design',   desc: 'Build strategies, systems, structures, and operating models.' },
  { key: 'deliver',  number: '04', title: 'Deliver',  desc: 'Implement through governance, PMO, and leadership support.' },
  { key: 'develop',  number: '05', title: 'Develop',  desc: 'Measure performance and continuously improve.' },
];

const principles = [
  'Data before assumptions.',
  'Systems before shortcuts.',
  'Execution before presentations.',
  'Long-term partnerships over one-time engagements.',
  'Business outcomes over theoretical recommendations.',
];

export default function Approach() {
  return (
    <section
      id="approach"
      data-testid={TID.approach.root}
      className="py-24 md:py-32 border-t border-white/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow mb-6">The Framework</p>
            <h2
              className="font-display font-semibold text-text tracking-tight leading-[1.02]"
              style={{ fontSize: 'clamp(36px, 5.4vw, 84px)' }}
            >
              A structured consulting{' '}
              <span className="text-ember">framework.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="max-w-md text-[15px] leading-relaxed text-text/75">
              Five stages, one operating rhythm — designed so strategy,
              teams, and technology move together toward outcomes that can
              be measured.
            </p>
          </div>
        </div>

        {/* Steps horizontal cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4">
          {steps.map((step, i) => (
            <div
              key={step.key}
              data-testid={TID.approach.step(i + 1)}
              className="group relative rounded-2xl border border-white/8 bg-ink-2 p-5 md:p-6 hover:border-ember/50 transition-colors flex flex-col justify-between min-h-[220px]"
            >
              <div className="flex items-start justify-between">
                <span className="font-display font-semibold text-ember text-lg">
                  {step.number}
                </span>
                <span className="text-[10px] uppercase tracking-[0.24em] text-text-70">
                  Step
                </span>
              </div>
              <div className="mt-6">
                <h3 className="font-display font-semibold text-text text-2xl md:text-3xl leading-tight">
                  {step.title}
                </h3>
                <p className="mt-3 text-[13px] leading-relaxed text-text/70">
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Working principles */}
        <div
          data-testid={TID.approach.principles}
          className="mt-20 md:mt-28 grid grid-cols-12 gap-8"
        >
          <div className="col-span-12 md:col-span-4">
            <p className="eyebrow mb-6">Working Principles</p>
            <h3
              className="font-display font-semibold text-text tracking-tight leading-[1.02]"
              style={{ fontSize: 'clamp(28px, 3.4vw, 48px)' }}
            >
              How I <span className="text-ember">work.</span>
            </h3>
          </div>
          <ul className="col-span-12 md:col-span-8 divide-y divide-white/8 border-t border-white/8">
            {principles.map((p, i) => (
              <li
                key={p}
                className="py-5 flex items-baseline gap-6 text-text/90 group"
              >
                <span className="font-display font-semibold text-ember w-10 text-sm">
                  0{i + 1}
                </span>
                <span className="font-display font-medium text-xl md:text-2xl leading-snug group-hover:text-ember transition-colors">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
